import { BaseModel } from '@/api/base-model'
import { AuthClient } from '@/api/auth-client'
import { PageResult } from '@/api/types'

export class RestClient<T extends BaseModel<T>> extends AuthClient {
    protected collectionName!: string
    protected parseData!: (data: any) => T
    cache: Map<string, T> = new Map<string, T>()

    async get(id: string, fresh: boolean = false): Promise<T> {
        try {
            if (!fresh && this.cache.has(id)) {
                return this.cache.get(id)
            }

            const response = await this.client.get(`${this.basePath}/${this.collectionName}/${id}`)
            const data = this.parseData(response.data) as T

            this.cache.set(id, data)
            return this.cache.get(id)

        } catch (error) {
            this.handleError(error)
        }
    }

    async getAll(params: any = {}): Promise<T[]> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}`, {
                params,
            })

            const collection = response.data['hydra:member'] as any[]
            return collection.map(value => this.parseData(value))

        } catch (error) {
            this.handleError(error)
        }
    }

    async getPaged(params: any): Promise<PageResult<T>> {
        try {
            const response = await this.client.get(`${this.basePath}/${this.collectionName}`, {
                params,
            })

            const totalItems = response.data['hydra:totalItems']
            const totalPages = Math.ceil(totalItems / (params.itemsPerPage || 10))
            const collection = (response.data['hydra:member'] as any[])
                .map(value => this.parseData(value))

            return {
                collection,
                totalItems,
                totalPages,
            }

        } catch (error) {
            this.handleError(error)
        }
    }

    async create(data: T): Promise<T> {
        try {
            const response = await this.client.post(`${this.basePath}/${this.collectionName}`, data)
            return await this.get(data.id, true)

        } catch (error) {
            this.handleError(error)
        }
    }

    async update(id: string, data: any): Promise<T> {
        try {
            const response = await this.client.put(`${this.basePath}/${this.collectionName}/${id}`, data)
            return await this.get(id, true)
        } catch (error) {
            this.handleError(error)
        }
    }

    async delete(id: string): Promise<void> {
        try {
            await this.client.delete(`${this.basePath}/${this.collectionName}/${id}`)
        } catch (error) {
            this.handleError(error)
        }
    }

    protected handleError(error: any) {
        if (error.response) {

            if ('hydra:description' in error.response.data) {
                console.log(error)
                // throw Error(error.response.data['hydra:description'])

            } else {

                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.log(error.response.data)
                console.log(error.response.status)
                console.log(error.response.headers)
                console.log(error.config)
            }

        } else if (error.request) {
            // The request was made but no response was received
            // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
            // http.ClientRequest in node.js
            console.log(error.request)
            console.log(error.config)

        } else {
            // Something happened in setting up the request that triggered an Error
            console.log('Error', error.message)
            console.log(error.config)
        }
    }
}
